import { loadStripe } from '@stripe/stripe-js';
import { FormData } from '../types';
import { AD_PRICE_CENTS } from '../utils/config';

class StripeService {
  private stripePromise: Promise<any>;
  private publishableKey: string;

  constructor() {
    this.publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
    this.stripePromise = loadStripe(this.publishableKey);
  }

  private validateConfig(): boolean {
    if (!this.publishableKey) {
      console.error('Stripe publishable key not configured');
      return false;
    }
    return true;
  }

  async createCheckoutSession(adId: string, formData: FormData): Promise<{sessionId: string; url: string} | null> {
    if (!this.validateConfig()) {
      return null;
    }

    try {
      const checkoutData = {
        ad_id: adId,
        amount: AD_PRICE_CENTS, // Dynamic price in cents
        currency: 'usd',
        ad_content: formData.adTextContent.substring(0, 100),
        success_url: `${window.location.origin}/payment-success?ad_id=${adId}&payment_method=stripe&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/payment-cancel?ad_id=${adId}&payment_method=stripe&session_id={CHECKOUT_SESSION_ID}`,
        metadata: {
          ad_id: adId,
          ad_content: formData.adTextContent.substring(0, 500),
          button_text: formData.buttonText || '',
          button_link: formData.buttonLink || '',
          has_image: formData.imagePreview ? 'true' : 'false'
        }
      };

      console.log('Creating Stripe checkout session with data:', checkoutData);
      console.log('🔍 SUCCESS URL:', checkoutData.success_url);
      console.log('🔍 CANCEL URL:', checkoutData.cancel_url);
      console.log('🔍 Current origin:', window.location.origin);

      // Call our Netlify function to create the checkout session
      const response = await fetch('/api/create-stripe-checkout', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(checkoutData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Stripe API error: ${response.status} - ${errorData.error || errorData.details}`);
      }

      const result = await response.json();
      console.log('Stripe checkout session created:', result.sessionId);
      console.log('🔍 Stripe checkout URL:', result.url);
      
      return {
        sessionId: result.sessionId,
        url: result.url
      };
      
    } catch (error) {
      console.error('Error creating Stripe checkout session:', error);
      return null;
    }
  }

  async redirectToCheckout(sessionId: string): Promise<{ error?: any }> {
    if (!this.validateConfig()) {
      return { error: 'Stripe not configured' };
    }

    try {
      const stripe = await this.stripePromise;
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      console.log('Redirecting to Stripe checkout with session:', sessionId);
      
      // Redirect to Stripe Checkout
      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        console.error('Stripe redirect error:', error);
        return { error };
      }
      
      return {};
      
    } catch (error) {
      console.error('Error redirecting to Stripe checkout:', error);
      return { error };
    }
  }

  async handlePaymentSuccess(sessionId: string): Promise<boolean> {
    try {
      // The payment verification is now handled by our Stripe webhook
      // This function mainly serves to log success on the client side
      console.log('Stripe payment successful for session:', sessionId);
      return true;
      
    } catch (error) {
      console.error('Error handling payment success:', error);
      return false;
    }
  }

  // Test connection
  async testConnection(): Promise<boolean> {
    if (!this.validateConfig()) {
      return false;
    }

    try {
      const stripe = await this.stripePromise;
      if (stripe) {
        console.log('Stripe loaded successfully');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error testing Stripe connection:', error);
      return false;
    }
  }
}

// Export singleton instance
export const stripeService = new StripeService();
export default stripeService; 